import type { Component } from 'solid-js'
import { createEffect, createMemo, createSignal, createUniqueId, For, Match, mergeProps, Show, Switch, splitProps } from 'solid-js'
import { twMerge } from 'tailwind-merge'
import Button from '~/components/Button'
import Icon from '~/components/Icon'
import { useFieldError } from '~/hooks/useFieldError'
import type {
  FileInputButtonPropsType,
  FileInputDropzonePropsType,
  FileInputFileItemPropsType,
  FileInputFileType,
  FileInputModeType,
  FileInputProgressPropsType,
  FileInputPropsType,
  FileInputSizeType,
  FileInputStatusType,
  FileInputToastPropsType
} from '~/types/FileInput'
import { controlSize } from '~/utils/controlSize'
import FieldMessage from './FieldMessage'
import { useFormContext } from './Form'

/* ─── Helpers ─── */
const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1048576).toFixed(1)} MB`
}

const generateId = (): string => Math.random().toString(36).slice(2, 11)

const isImageType = (type: string): boolean => type.startsWith('image/')

/* ─── Status config ─── */
const statusConfig: Record<FileInputStatusType, { color: string; icon: string; label: string }> = {
  pending: { color: 'text-foreground-subtle', icon: 'material-symbols-light:schedule', label: 'Pendiente' },
  uploading: { color: 'text-info', icon: 'material-symbols-light:cloud-upload', label: 'Subiendo...' },
  complete: { color: 'text-success', icon: 'material-symbols-light:check-circle', label: 'Completado' },
  error: { color: 'text-danger', icon: 'material-symbols-light:error', label: 'Error' },
  paused: { color: 'text-warning', icon: 'material-symbols-light:pause-circle', label: 'Pausado' }
}

/* ─── Spinner ─── */
const Spinner: Component<{ size?: number; class?: string }> = props => {
  const p = mergeProps({ size: 20 }, props)
  return (
    <svg class={twMerge('animate-spin', p.class)} width={p.size} height={p.size} viewBox="0 0 24 24" fill="none">
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  )
}

/* ─── Progress Bar ─── */
const ProgressBar: Component<{ progress: number; class?: string }> = props => {
  const p = mergeProps({ progress: 0 }, props)
  const width = createMemo(() => `${Math.min(100, Math.max(0, p.progress))}%`)

  return (
    <div class={twMerge('h-1.5 w-full rounded-full bg-surface-muted overflow-hidden', p.class)}>
      <div class="h-full rounded-full bg-info transition-all duration-300 ease-out" style={{ width: width() }} />
    </div>
  )
}

/* ─── Toast ─── */
const Toast: Component<FileInputToastPropsType> = props => {
  const [local, others] = splitProps(props, ['message', 'variant', 'onClose', 'class'])

  const variant = () => local.variant ?? 'success'
  const variantClasses = createMemo(() =>
    variant() === 'success' ? 'bg-success-soft border-success/20 text-success' : variant() === 'error' ? 'bg-danger-soft border-danger/20 text-danger' : 'bg-info-soft border-info/20 text-info'
  )

  return (
    <div class={twMerge('flex items-center gap-3 px-4 py-3 rounded-xl border backdrop-blur-sm', variantClasses(), local.class)} {...others}>
      <Icon icon={variant() === 'success' ? 'material-symbols-light:check-circle' : variant() === 'error' ? 'material-symbols-light:error' : 'material-symbols-light:info'} size={20} />
      <span class="flex-1 text-sm font-medium">{local.message}</span>
      <Show when={local.onClose}>
        <button type="button" onClick={local.onClose} class="text-sm font-medium opacity-70 hover:opacity-100 transition-opacity">
          Cerrar
        </button>
      </Show>
    </div>
  )
}

/* ─── File Item ─── */
const FileItem: Component<FileInputFileItemPropsType> = props => {
  const [local, others] = splitProps(props, ['file', 'onPause', 'onResume', 'onCancel', 'onRemove', 'class'])

  const config = () => statusConfig[local.file.status]

  return (
    <div
      class={twMerge(
        'flex items-center gap-3 p-3 rounded-xl bg-surface border border-border transition-all duration-200',
        'hover:border-border-strong hover:shadow-sm',
        local.file.status === 'complete' && 'border-success/30',
        local.file.status === 'error' && 'border-danger/30',
        local.class
      )}
      {...others}
    >
      {/* Thumbnail */}
      <div class="w-10 h-10 rounded-lg overflow-hidden bg-surface-muted flex items-center justify-center shrink-0">
        <Show when={local.file.preview && isImageType(local.file.type)} fallback={<Icon icon="material-symbols-light:description-outline" size={20} class="text-foreground-subtle" />}>
          <img src={local.file.preview} alt={local.file.name} class="w-full h-full object-cover" />
        </Show>
      </div>

      {/* Info */}
      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-2">
          <span class="text-sm font-medium text-foreground truncate">{local.file.name}</span>
          <Show when={local.file.status === 'uploading'}>
            <Spinner size={14} class="text-info shrink-0" />
          </Show>
        </div>
        <div class="flex items-center gap-2 mt-0.5">
          <span class="text-xs text-foreground-subtle">{formatSize(local.file.size)}</span>
          <Show when={local.file.status === 'uploading'}>
            <span class="text-xs text-info">{local.file.progress}%</span>
          </Show>
          <Show when={local.file.status !== 'uploading'}>
            <span class={twMerge('text-[10px] font-medium uppercase tracking-wide', config().color)}>{config().label}</span>
          </Show>
          <Show when={local.file.status === 'error' && local.file.error}>
            <span class="text-xs text-danger">{local.file.error}</span>
          </Show>
        </div>
        <Show when={local.file.status === 'uploading'}>
          <ProgressBar progress={local.file.progress} class="mt-1.5" />
        </Show>
      </div>

      {/* Actions */}
      <div class="flex items-center gap-1 shrink-0">
        <Show when={local.file.status === 'uploading'}>
          <button type="button" onClick={local.onPause} class="p-1.5 rounded-lg hover:bg-surface-muted text-foreground-subtle hover:text-foreground transition-colors" title="Pausar">
            <Icon icon="material-symbols-light:pause" size={16} />
          </button>
        </Show>
        <Show when={local.file.status === 'paused'}>
          <button type="button" onClick={local.onResume} class="p-1.5 rounded-lg hover:bg-surface-muted text-foreground-subtle hover:text-foreground transition-colors" title="Reanudar">
            <Icon icon="material-symbols-light:play-arrow" size={16} />
          </button>
        </Show>
        <Show when={local.file.status === 'uploading' || local.file.status === 'paused'}>
          <button type="button" onClick={local.onCancel} class="p-1.5 rounded-lg hover:bg-surface-muted text-foreground-subtle hover:text-danger transition-colors" title="Cancelar">
            <Icon icon="material-symbols-light:close" size={16} />
          </button>
        </Show>
        <Show when={local.file.status === 'complete' || local.file.status === 'error'}>
          <button type="button" onClick={local.onRemove} class="p-1.5 rounded-lg hover:bg-surface-muted text-foreground-subtle hover:text-danger transition-colors" title="Eliminar">
            <Icon icon="material-symbols-light:delete-outline" size={16} />
          </button>
        </Show>
      </div>
    </div>
  )
}

/* ─── Progress Widget ─── */
const ProgressWidget: Component<FileInputProgressPropsType> = props => {
  const [local, others] = splitProps(props, ['totalFiles', 'completedFiles', 'progress', 'status', 'onPause', 'onResume', 'onCancel', 'class'])

  return (
    <div class={twMerge('flex items-center gap-4 p-4 rounded-2xl bg-surface border border-border shadow-lg', local.class)} {...others}>
      {/* Badge */}
      <div class="w-10 h-10 rounded-xl bg-info-soft flex items-center justify-center shrink-0">
        <span class="text-sm font-bold text-info">{local.totalFiles}</span>
      </div>

      {/* Info + Progress */}
      <div class="flex-1 min-w-0 space-y-1.5">
        <div class="flex items-center gap-2">
          <span class="text-sm font-semibold text-foreground">
            {local.status === 'uploading' ? 'Subiendo...' : local.status === 'paused' ? 'Pausado' : local.status === 'complete' ? 'Completado' : 'Error'}
          </span>
          <Show when={local.status === 'uploading'}>
            <Spinner size={14} class="text-info" />
          </Show>
        </div>
        <div class="flex items-center gap-3">
          <span class="text-xs text-foreground-subtle font-medium">
            {local.completedFiles}/{local.totalFiles}
          </span>
          <ProgressBar progress={local.progress} class="flex-1" />
          <span class="text-xs font-semibold text-foreground-muted">{Math.round(local.progress)}%</span>
        </div>
      </div>

      {/* Controls */}
      <div class="flex items-center gap-1 shrink-0">
        <Show when={local.status === 'uploading'}>
          <button type="button" onClick={local.onPause} class="p-2 rounded-xl hover:bg-surface-muted text-foreground-subtle hover:text-foreground transition-colors" title="Pausar todo">
            <Icon icon="material-symbols-light:pause" size={18} />
          </button>
        </Show>
        <Show when={local.status === 'paused'}>
          <button type="button" onClick={local.onResume} class="p-2 rounded-xl hover:bg-surface-muted text-foreground-subtle hover:text-foreground transition-colors" title="Reanudar todo">
            <Icon icon="material-symbols-light:play-arrow" size={18} />
          </button>
        </Show>
        <button type="button" onClick={local.onCancel} class="p-2 rounded-xl hover:bg-surface-muted text-foreground-subtle hover:text-danger transition-colors" title="Cancelar">
          <Icon icon="material-symbols-light:close" size={18} />
        </button>
      </div>
    </div>
  )
}

/* ─── Input Trigger (estilo input + drag zone) ─── */
const InputTrigger: Component<{
  disabled?: boolean
  count: number
  size?: FileInputSizeType
  accept?: string
  multiple?: boolean
  onFilesSelected?: (files: File[]) => void
  class?: string
}> = props => {
  const [isDragOver, setIsDragOver] = createSignal(false)
  let inputRef: HTMLInputElement | undefined

  /**
   * El input vive dentro de este div, así que el click sintético de `input.click()`
   * burbujeaba de vuelta y abría el diálogo DOS veces. Se ignora el clic que
   * proviene del propio input.
   */
  const handleClick = (e?: MouseEvent) => {
    if (props.disabled) return
    if (e && e.target === inputRef) return
    inputRef?.click()
  }

  const handleFiles = (fileList: FileList | null) => {
    if (fileList && props.onFilesSelected) props.onFilesSelected(Array.from(fileList))
  }

  return (
    <div
      class={twMerge(
        'flex items-center gap-2 w-full rounded-xl border border-border bg-surface cursor-pointer',
        // Cubría sólo sm/md/lg y con medidas propias; ahora sigue la escala común.
        controlSize(props.size).height,
        controlSize(props.size).padX,
        'transition-colors hover:border-primary/50 hover:bg-primary/5',
        isDragOver() && 'border-info bg-info/5',
        props.disabled && 'opacity-50 cursor-not-allowed',
        props.class
      )}
      onClick={handleClick}
      onDragOver={e => {
        e.preventDefault()
        if (!props.disabled) setIsDragOver(true)
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={e => {
        e.preventDefault()
        setIsDragOver(false)
        if (!props.disabled) handleFiles(e.dataTransfer?.files ?? null)
      }}
      role="button"
      tabindex={props.disabled ? -1 : 0}
      aria-label="Seleccionar archivos"
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleClick()
        }
      }}
    >
      <input
        ref={inputRef}
        type="file"
        class="sr-only"
        accept={props.accept}
        multiple={props.multiple}
        onClick={e => {
          // Limpiar el valor permite volver a elegir el mismo archivo.
          e.currentTarget.value = ''
        }}
        onChange={e => handleFiles(e.target.files)}
      />
      <span class={twMerge('w-8 h-8 rounded-lg bg-surface-muted flex items-center justify-center shrink-0', isDragOver() ? 'text-info' : 'text-foreground-subtle')}>
        <Icon icon="material-symbols-light:attach-file" size={18} />
      </span>
      <span class="flex-1 text-sm truncate text-foreground-subtle">{isDragOver() ? 'Suelta los archivos aquí' : 'Seleccionar archivos'}</span>
      <span class="text-xs font-medium text-foreground-muted bg-surface-muted rounded-full px-2 py-0.5 shrink-0">{props.count}</span>
    </div>
  )
}

/* ─── Button Trigger (sólo un botón, con zona de soltado alrededor) ─── */
const ButtonTrigger: Component<FileInputButtonPropsType> = props => {
  const [local, others] = splitProps(props, ['size', 'disabled', 'accept', 'multiple', 'label', 'icon', 'color', 'variant', 'count', 'onFilesSelected', 'class'])
  const [isDragOver, setIsDragOver] = createSignal(false)
  let inputRef: HTMLInputElement | undefined

  const handleFiles = (fileList: FileList | null) => {
    if (fileList && local.onFilesSelected) local.onFilesSelected(Array.from(fileList))
  }

  return (
    <div
      class={twMerge('inline-flex items-center gap-2 rounded-xl transition-colors', isDragOver() && 'ring-2 ring-info ring-offset-2 ring-offset-background', local.class)}
      onDragOver={e => {
        e.preventDefault()
        if (!local.disabled) setIsDragOver(true)
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={e => {
        e.preventDefault()
        setIsDragOver(false)
        if (!local.disabled) handleFiles(e.dataTransfer?.files ?? null)
      }}
      {...others}
    >
      <input
        ref={inputRef}
        type="file"
        class="sr-only"
        accept={local.accept}
        multiple={local.multiple}
        onClick={e => {
          // Limpiar el valor permite volver a elegir el mismo archivo.
          e.currentTarget.value = ''
        }}
        onChange={e => handleFiles(e.target.files)}
      />
      <Button
        type="button"
        variant={local.variant ?? 'outlined'}
        color={local.color ?? 'primary'}
        size={local.size ?? 'md'}
        disabled={local.disabled}
        startIcon={local.icon ?? 'material-symbols-light:upload'}
        onClick={() => inputRef?.click()}
      >
        {isDragOver() ? 'Suelta los archivos aquí' : (local.label ?? 'Seleccionar archivos')}
      </Button>
      <Show when={local.count}>
        <span class="text-xs font-medium text-foreground-muted bg-surface-muted rounded-full px-2 py-0.5 shrink-0">{local.count}</span>
      </Show>
    </div>
  )
}

/* ─── Dropzone ─── */
const Dropzone: Component<FileInputDropzonePropsType> = props => {
  const [local, others] = splitProps(props, ['variant', 'size', 'disabled', 'accept', 'multiple', 'hint', 'onFilesSelected', 'class'])
  const [isDragOver, setIsDragOver] = createSignal(false)
  let inputRef: HTMLInputElement | undefined

  const variant = () => local.variant ?? 'dashed'
  const size = () => local.size ?? 'md'

  const sizeClasses = createMemo(() => {
    const map: Record<string, string> = {
      sm: 'p-4',
      md: 'p-8',
      lg: 'p-12'
    }
    return map[size()]
  })

  const variantClasses = createMemo(() => {
    const base = 'border-2 border-dashed rounded-2xl transition-all duration-200 cursor-pointer'
    const v = variant()
    if (isDragOver()) return `${base} border-info bg-info/5 scale-[1.01]`
    if (v === 'outlined') return `${base} border-border hover:border-primary/50 hover:bg-primary/5`
    if (v === 'dashed') return `${base} border-border-strong/40 hover:border-primary/50 hover:bg-primary/5`
    return `${base} border-border hover:border-primary/50 hover:bg-primary/5`
  })

  /** Ver la nota del InputTrigger: sin este guard el diálogo se abría dos veces. */
  const handleClick = (e?: MouseEvent) => {
    if (local.disabled) return
    if (e && e.target === inputRef) return
    inputRef?.click()
  }

  const handleFiles = (fileList: FileList | null) => {
    if (!fileList || !local.onFilesSelected) return
    local.onFilesSelected(Array.from(fileList))
  }

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault()
    if (!local.disabled) setIsDragOver(true)
  }

  const handleDragLeave = () => setIsDragOver(false)

  const handleDrop = (e: DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    if (!local.disabled) handleFiles(e.dataTransfer?.files ?? null)
  }

  return (
    <div
      class={twMerge(variantClasses(), sizeClasses(), local.disabled && 'opacity-50 cursor-not-allowed', local.class)}
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      role="button"
      tabindex={local.disabled ? -1 : 0}
      aria-label="Zona de carga de archivos"
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleClick()
        }
      }}
      {...others}
    >
      <input
        ref={inputRef}
        type="file"
        class="sr-only"
        accept={local.accept}
        multiple={local.multiple}
        onClick={e => {
          // Limpiar el valor permite volver a elegir el mismo archivo.
          e.currentTarget.value = ''
        }}
        onChange={e => handleFiles(e.target.files)}
      />
      <div class="flex flex-col items-center gap-3 text-center">
        <div class={twMerge('w-12 h-12 rounded-2xl flex items-center justify-center transition-colors', isDragOver() ? 'bg-info/10 text-info' : 'bg-surface-muted text-foreground-subtle')}>
          <Icon icon="material-symbols-light:cloud-upload-outline" size={28} />
        </div>
        <div>
          <p class="text-sm font-semibold text-foreground">{isDragOver() ? 'Suelta los archivos aquí' : 'Arrastra archivos o haz clic para seleccionar'}</p>
          <p class="text-xs text-foreground-subtle mt-1">{local.hint ?? 'Cualquier tipo de archivo'}</p>
        </div>
      </div>
    </div>
  )
}

/* ─── Main FileInput ─── */
const FileInputBase: Component<FileInputPropsType> = props => {
  const [local, others] = splitProps(props, [
    'variant',
    'size',
    'mode',
    'appearance',
    'buttonLabel',
    'buttonIcon',
    'buttonColor',
    'buttonVariant',
    'hideFileList',
    'name',
    'label',
    'error',
    'helperText',
    'accept',
    'multiple',
    'maxFiles',
    'maxSize',
    'disabled',
    'files',
    'onChange',
    'onUpload',
    'onPause',
    'onResume',
    'onCancel',
    'onRemove',
    'class'
  ])

  const formCtx = useFormContext()

  /** `appearance` sigue funcionando mientras no se indique `mode`. */
  const mode = (): FileInputModeType => local.mode ?? local.appearance ?? 'dropzone'

  const getInitialFiles = (): FileInputFileType[] => {
    if (local.files) return local.files
    if (local.name && formCtx) {
      const iv = formCtx.initialValues[local.name]
      if (Array.isArray(iv)) return iv as FileInputFileType[]
    }
    return []
  }

  const [internalFiles, setInternalFiles] = createSignal<FileInputFileType[]>(getInitialFiles())
  const currentFiles = () => local.files ?? internalFiles()
  /** Aviso propio cuando un archivo excede `maxSize` (el `error` de props es del formulario). */
  const [sizeError, setSizeError] = createSignal('')

  // Sin prop `error`, el campo lee su error del contexto del `<Form>`. La prop
  // explícita gana sobre el contexto, y un archivo que pasa de `maxSize` sobre
  // las dos: es lo más concreto que se le puede decir a quien lo acaba de soltar.
  const resolvedError = useFieldError(
    () => local.name,
    () => sizeError() || local.error
  )

  /** Id para asociar el label al input file real (`sr-only`). */
  const labelForId = createUniqueId()

  /**
   * Input real con `name` que el navegador incluye en `new FormData(form)`.
   *
   * Antes esto era un `<input type="hidden">` con `JSON.stringify(currentFiles())`,
   * y como un `File` se serializa a `{}`, el binario NUNCA llegaba al servidor:
   * el campo viajaba con los metadatos y sin contenido. Ahora se mantiene una
   * `FileList` de verdad, reconstruida con `DataTransfer` cada vez que cambia la
   * lista visible, para que quitar un archivo también lo quite del envío.
   */
  let nativeRef: HTMLInputElement | undefined

  let lastEmitted: string | null = null
  let firstRun = true
  createEffect(() => {
    const files = currentFiles()
    const current = JSON.stringify(files.map(f => [f.name, f.size, f.type]))
    if (!local.name) return

    if (nativeRef) {
      const transfer = new DataTransfer()
      for (const f of files) transfer.items.add(f.file)
      nativeRef.files = transfer.files
    }

    if (current !== lastEmitted) {
      lastEmitted = current
      // El primer paso sólo sincroniza; tocar/revalidar se reserva a los
      // cambios reales del usuario para no mostrar errores al abrir el form.
      if (firstRun) {
        firstRun = false
      } else {
        if (fieldName) formCtx?.touchField(fieldName)
        formCtx?.revalidate()
      }
    }
  })

  // No se registra con `registerField`: el Form serializa el valor de los campos
  // registrados con JSON.stringify, y eso sobrescribiría el File real del input
  // de arriba dejando el binario fuera del envío.
  const fieldName = local.name

  const addFiles = (newFiles: File[]) => {
    const accepted: File[] = []
    for (const file of newFiles) {
      // `maxSize` se declaraba pero no se aplicaba: un archivo enorme entraba
      // igual y sólo fallaba al llegar al servidor.
      if (local.maxSize && file.size > local.maxSize) {
        setSizeError(`"${file.name}" supera el máximo de ${formatSize(local.maxSize)}`)
        continue
      }
      accepted.push(file)
    }
    if (!accepted.length) return
    setSizeError('')

    const processed: FileInputFileType[] = accepted.map(file => ({
      id: generateId(),
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      preview: isImageType(file.type) ? URL.createObjectURL(file) : undefined,
      status: 'pending' as const,
      progress: 0
    }))

    // Sin `multiple`, cada selección sustituye a la anterior en vez de acumular.
    const base = local.multiple ? currentFiles() : []
    const merged = [...base, ...processed].slice(0, local.maxFiles ?? Infinity)
    setInternalFiles(merged)
    local.onChange?.(merged)
  }

  const removeFile = (id: string) => {
    const filtered = currentFiles().filter(f => f.id !== id)
    setInternalFiles(filtered)
    local.onChange?.(filtered)
    local.onRemove?.(id)
  }

  const uploadingFiles = () => currentFiles().filter(f => f.status === 'uploading' || f.status === 'paused')
  const completedFiles = () => currentFiles().filter(f => f.status === 'complete')
  const totalProgress = () => {
    const files = currentFiles()
    if (files.length === 0) return 0
    return files.reduce((sum, f) => sum + f.progress, 0) / files.length
  }
  const globalStatus = (): FileInputStatusType => {
    const files = currentFiles()
    if (files.length === 0) return 'pending'
    if (files.some(f => f.status === 'error')) return 'error'
    if (files.some(f => f.status === 'paused')) return 'paused'
    if (files.every(f => f.status === 'complete')) return 'complete'
    return 'uploading'
  }

  // `@container`: la disposición depende del ancho DISPONIBLE, no del de la
  // ventana. Con `sm:` el panel de archivos se salía de los modales estrechos,
  // porque el breakpoint miraba el viewport y creía tener sitio de sobra.
  return (
    <div class={twMerge('@container w-full', local.class)} {...others}>
      <Show when={local.label}>
        <label for={labelForId} class="block text-sm font-medium text-foreground mb-1.5">
          {local.label}
        </label>
      </Show>

      <Show when={local.name}>
        <input ref={nativeRef} id={labelForId} type="file" name={local.name} class="sr-only" tabindex={-1} accept={local.accept} multiple={local.multiple} />
      </Show>

      <div class="flex flex-col @lg:flex-row gap-4 items-start">
        {/* Disparador según el modo. En `button` la columna se ajusta al botón. */}
        <div class={twMerge('shrink-0', mode() !== 'button' && (local.hideFileList ? 'w-full' : 'w-full @lg:w-72'))}>
          <Show when={!local.disabled}>
            <Switch fallback={<Dropzone variant={local.variant} size={local.size ?? 'sm'} accept={local.accept} multiple={local.multiple} onFilesSelected={addFiles} />}>
              <Match when={mode() === 'input'}>
                <InputTrigger count={currentFiles().length} size={local.size} accept={local.accept} multiple={local.multiple} onFilesSelected={addFiles} />
              </Match>
              <Match when={mode() === 'button'}>
                <ButtonTrigger
                  count={currentFiles().length}
                  size={local.size}
                  accept={local.accept}
                  multiple={local.multiple}
                  label={local.buttonLabel}
                  icon={local.buttonIcon}
                  color={local.buttonColor}
                  variant={local.buttonVariant}
                  onFilesSelected={addFiles}
                />
              </Match>
            </Switch>
          </Show>
        </div>

        {/* Panel de archivos cargados */}
        <div class={twMerge('flex-1 min-w-0 w-full rounded-2xl border border-border bg-surface overflow-hidden', local.hideFileList && 'hidden')}>
          <div class="flex items-center justify-between px-4 py-3 border-b border-border bg-surface-muted/40">
            <span class="text-sm font-semibold text-foreground">Archivos cargados</span>
            <span class="text-xs font-medium text-foreground-subtle bg-surface-muted rounded-full px-2 py-0.5">{currentFiles().length}</span>
          </div>

          <Show
            when={currentFiles().length > 0}
            fallback={
              <div class="flex flex-col items-center justify-center gap-2 py-10 text-center px-4">
                <Icon icon="material-symbols-light:folder-open-outline" size={28} class="text-foreground-subtle" />
                <p class="text-sm text-foreground-subtle">Aún no hay archivos seleccionados</p>
              </div>
            }
          >
            <div class="p-3 space-y-2 max-h-80 overflow-y-auto">
              <For each={currentFiles()}>
                {file => (
                  <FileItem
                    file={file}
                    onPause={() => local.onPause?.(file.id)}
                    onResume={() => local.onResume?.(file.id)}
                    onCancel={() => (local.onCancel ? local.onCancel(file.id) : removeFile(file.id))}
                    onRemove={() => removeFile(file.id)}
                  />
                )}
              </For>
            </div>
          </Show>
        </div>

        <Show when={uploadingFiles().length > 0}>
          <ProgressWidget
            totalFiles={uploadingFiles().length}
            completedFiles={completedFiles().length}
            progress={totalProgress()}
            status={globalStatus()}
            onPause={() => uploadingFiles().forEach(f => local.onPause?.(f.id))}
            onResume={() => uploadingFiles().forEach(f => local.onResume?.(f.id))}
            onCancel={() =>
              uploadingFiles().forEach(f => {
                if (local.onCancel) local.onCancel(f.id)
                else removeFile(f.id)
              })
            }
          />
        </Show>
      </div>

      <FieldMessage size={local.size} error={resolvedError()} helperText={local.helperText} />
    </div>
  )
}

/* ─── Compound export ─── */
const FileInput = Object.assign(FileInputBase, {
  Dropzone,
  Button: ButtonTrigger,
  Input: InputTrigger,
  FileItem,
  Progress: ProgressWidget,
  Toast
})

export default FileInput
